import { Infer, t } from 'typegate';
import { underwritingRequirementsSchema } from '../models/underwritingRequirements';
import { participantMedicalInterviewSchema } from '../participantMedicalInterview';

const participantDataSchema = t
  .object(
    t.optionalProperty('firstName', t.string),
    t.optionalProperty('lastName', t.string),
    t.optionalProperty('email', t.string),
  )
  .setTypeName('ParticipantData');

export type ParticipantData = Infer<typeof participantDataSchema>;

export const participantRoleNameSchema = t.union(
  t.literal('INSURED'),
  t.literal('PRIMARY_OWNER'),
  t.literal('SECONDARY_OWNER'),
  t.literal('OWNER'),
  t.literal('LEGAL_GUARDIAN'),
  t.literal('DEPENDENT_CHILD'),
  t.literal('PAYER'),
  t.literal('BENEFICIARY'),
);

export type ParticipantRoleName = Infer<typeof participantRoleNameSchema>;

/**
 * Describes the Participant stored in the db
 */

export const participantSchema = t
  .object(
    t.property('id', t.string),
    t.property('applicationId', t.string),
    t.property('roles', t.array(participantRoleNameSchema)),
    t.property('data', t.union(participantDataSchema, t.null)),
    t.optionalProperty('dependantParticipantIds', t.array(t.string)),
    t.optionalProperty('underwritingRequirements', t.array(underwritingRequirementsSchema)),
    t.optionalProperty('medicalInterview', participantMedicalInterviewSchema),
  )
  .setTypeName('Participant');

export type Participant = Infer<typeof participantSchema>;

const participantWithMedicalInterviewSchema = t
  .intersection(participantSchema, t.object(t.optionalProperty('medicalInterview', participantMedicalInterviewSchema)))
  .setTypeName('ParticipantWithMedicalInterview');

export type ParticipantWithMedicalInterview = Infer<typeof participantWithMedicalInterviewSchema>;
/**
 * Describes the payload we expect on the PATCH endpoint
 */

export const patchParticipantPayloadSchema = t
  .object(
    t.property('id', t.string),
    t.optionalProperty('roles', t.array(participantRoleNameSchema)),
    t.optionalProperty('data', t.union(participantDataSchema, t.null)),
    t.optionalProperty('dependantParticipantIds', t.array(t.string)),
  )
  .setTypeName('PatchParticipantPayload');

export type PatchParticipantPayload = Infer<typeof patchParticipantPayloadSchema>;

/**
 * Describes the payload we expect on the CREATE endpoint
 */

const createParticipantSchema = t
  .object(
    t.property('id', t.string),
    t.property('applicationId', t.string),
    t.property('roles', t.array(participantRoleNameSchema)),
    t.property('data', t.union(participantDataSchema, t.null)),
  )
  .setTypeName('CreateParticipant');

export type CreateParticipant = Infer<typeof createParticipantSchema>;

/**
 * Signing participant roles (currently used for ESignature)
 * Todo: rename to something more akin to `SignerRoleType` and `SignerRole`
 * https://zinnia.atlassian.net/browse/HOT-5451
 */

export type ParticipantRoleType = {
  identifier: string;
  id: string;
  name: string;
  description?: string;
  createdAt?: Date;
  updatedAt?: Date;
  roles?: ParticipantRole[];
};

export type ParticipantRole = {
  id: string;
  type: string;
  position: number;
  name?: string;
  description?: string;
  deletedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
};

export const generateParticipantsDataSchema = t.object(
  t.property('answers', t.record(t.string, t.any)),
  t.property('answersV2', t.record(t.string, t.any)),
  t.property('applicationId', t.string),
  t.optionalProperty('withMedicalInterviews', t.boolean),
);

export type GenerateParticipantsData = Infer<typeof generateParticipantsDataSchema>;
